<template>
  <div id="app">
    <div class="container">
      <!-- Lista de procedimentos do paciente -->
      <b-card v-if="!loading && !showProcedimentoForm">
        <b-card-body>
          <b-list-group>
            <div class="justify-content-end d-flex mb-2 flex-wrap">
              <b-button
                variant="primary"
                @click="createProcedimento"
              >
                Inserir
              </b-button>
            </div>
            <div
              v-if="!hasProcedimentos"
              class="d-flex justify-content-center align-items-center"
            >
              <feather-icon
                icon="FileTextIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted">
                {{ emptyStateMessage }}
              </p>
            </div>
            <div v-else>
              <b-list-group-item
                v-for="procedimento in procedimentos"
                :key="procedimento.id"
                class="d-flex justify-content-between align-items-center"
              >
                <div>
                  <p>
                    <small>
                      <b> Data:</b>
                      {{ formatHyphenToSlash(procedimento.data) }}
                    </small>
                  </p>
                  <p>
                    <small>
                      <b> Profissional:</b>
                      {{ procedimento.profissional.nome }}
                    </small>
                  </p>
                </div>

                <div>
                  <b-button
                    v-if="grupo.nome.toLowerCase().includes('parecer cardi')"
                    variant="outline-primary"
                    size="sm"
                    class="mr-1"
                    title="Imprimir Parecer"
                    @click="imprimirParecer(procedimento)"
                  >
                    <feather-icon icon="PrinterIcon" />
                  </b-button>
                  <b-button
                    v-if="grupo.nome.toLowerCase().includes('receita')"
                    variant="outline-primary"
                    size="sm"
                    class="mr-1"
                    title="Imprimir Receita"
                    @click="imprimirReceita(procedimento)"
                  >
                    <feather-icon icon="PrinterIcon" />
                  </b-button>
                  <b-button
                    v-if="grupo.nome.toLowerCase().includes('exame')"
                    variant="outline-primary"
                    size="sm"
                    class="mr-1"
                    title="Imprimir Solicitação de Exame"
                    @click="imprimirExame(procedimento)"
                  >
                    <feather-icon icon="PrinterIcon" />
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mr-1"
                    title="Editar Procedimento"
                    @click="editProcedimento(procedimento)"
                  >
                    <feather-icon icon="EditIcon" />
                  </b-button>
                </div>
              </b-list-group-item>
            </div>
          </b-list-group>
        </b-card-body>
      </b-card>

      <!-- Loading spinner -->
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <b-spinner
          variant="primary"
          label="Carregando..."
        />
        <p class="mt-2 text-muted">
          Carregando procedimentos...
        </p>
      </div>

      <!-- Formulário de odontograma (será mostrado quando showOdontogramaForm = true) -->
      <div v-if="showProcedimentoForm">
        <b-row class="justify-content-end d-flex flex-wrap">
          <b-button
            class="mb-3"
            variant="outline-secondary"
            @click="backToList()"
          >
            <feather-icon icon="ArrowLeftIcon" />
            Voltar para Lista
          </b-button>
        </b-row>
        <ProcedimentoFormEdit
          :procedimento-data="procedimento"
          :grupo="grupo.id"
          :create="create"
          :procedimento-id="procedimentoId"
          @save="saveProcedimento"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BButton,
  BSpinner,
  BRow,
} from 'bootstrap-vue'
import ProcedimentoFormEdit from './ProcedimentoFormEdit.vue'

const TOAST_CONFIG = {
  title: 'Erro',
  variant: 'danger',
  solid: true,
}

export default {
  components: {
    BCard,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BButton,
    BSpinner,
    BRow,
    ProcedimentoFormEdit,
  },
  props: {
    procedimentos: {
      type: Array,
      required: true,
    },
    grupo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showProcedimentoForm: false,
      loading: false,
      create: false,
      procedimento: [],
      procedimentoId: null,
    }
  },
  computed: {
    hasProcedimentos() {
      return this.procedimentos && this.procedimentos.length > 0
    },
    emptyStateMessage() {
      return 'Nenhum procedimento encontrado para este paciente.'
    },
  },
  methods: {
    async createProcedimento() {
      this.loading = true

      try {
        const response = await this.$store.dispatch(
          'atendimentoProfissionalModule/create',
          {
            atendimento: this.$route.params.id,
            grupo: this.grupo.id,
          },
        )

        if (response.error) {
          this.showErrorToast(response.message)
          return
        }

        this.procedimento = response.data
        this.showProcedimentoForm = true
        this.create = true
      } catch (error) {
        this.showErrorToast('Erro ao criar procedimento')
        console.error('Error creating procedimento:', error)
      } finally {
        this.loading = false
      }
    },
    saveProcedimento(procedimentos) {
      this.procedimento = [...procedimentos]
    },
    async editProcedimento(procedimento) {
      this.loading = true

      try {
        const response = await this.$store.dispatch(
          'atendimentoProfissionalModule/getById',
          procedimento.id,
        )

        if (response.error) {
          this.showErrorToast(response.message)
          return
        }

        this.procedimentoId = procedimento.id
        this.procedimento = response.data
        this.showProcedimentoForm = true
        this.create = false
      } catch (error) {
        this.showErrorToast('Erro ao carregar procedimento')
        console.error('Error loading procedimento:', error)
      } finally {
        this.loading = false
      }
    },
    async backToList() {
      this.loading = true

      try {
        // Sempre recarrega os dados do backend
        await this.$emit('refresh-procedimentos', this.grupo.id)

        this.showProcedimentoForm = false
        this.procedimento = []
        this.procedimentoId = null
        this.create = false
      } catch (error) {
        this.showErrorToast('Erro ao atualizar lista')
        console.error('Error refreshing procedimentos:', error)
      } finally {
        this.loading = false
      }
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, TOAST_CONFIG)
    },
    formatHyphenToSlash(dateString) {
      if (!dateString) return ''

      if (
        typeof dateString !== 'string'
        || !/^\d{2}-\d{2}-\d{4}$/.test(dateString)
      ) {
        console.warn('Data não está no formato DD-MM-YYYY:', dateString)
        return dateString
      }

      return dateString.replace(/-/g, '/')
    },
    imprimirParecer(procedimento) {
      this.$bvToast.toast('Gerando parecer. Isso pode levar alguns segundos...', {
        title: 'Aguarde',
        variant: 'info',
        solid: true,
      })

      this.$store
        .dispatch('parecerCardiologicoModule/imprimirRelatorio', {
          atendimento_id: this.$route.params.id,
        })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(error => {
          console.error('Erro ao gerar PDF de Parecer:', error)
          this.$bvToast.toast('Falha ao gerar o parecer cardiológico.', {
            title: 'Erro de Emissão',
            variant: 'danger',
            solid: true,
          })
        })
    },
    imprimirReceita(procedimento) {
      this.$bvToast.toast('Gerando receita. Isso pode levar alguns segundos...', {
        title: 'Aguarde',
        variant: 'info',
        solid: true,
      })

      this.$store
        .dispatch('reportModule/printReportReceita', {
          paciente_procedimento_id: procedimento.id,
        })
        .then(response => {
          if (response && response.error) {
            this.$bvToast.toast(response.error, {
              title: 'Erro de Emissão',
              variant: 'danger',
              solid: true,
            })
          }
          // The action printReportReceita already handles file download via window.open and URL.createObjectURL
        })
        .catch(error => {
          console.error('Erro ao gerar PDF de Receita:', error)
          this.$bvToast.toast('Falha ao gerar a receita.', {
            title: 'Erro de Emissão',
            variant: 'danger',
            solid: true,
          })
        })
    },
    imprimirExame(procedimento) {
      this.$bvToast.toast('Gerando solicitação de exame. Isso pode levar alguns segundos...', {
        title: 'Aguarde',
        variant: 'info',
        solid: true,
      })

      this.$store
        .dispatch('reportModule/printReportExame', {
          paciente_procedimento_id: procedimento.id,
        })
        .then(response => {
          if (response && response.error) {
            this.$bvToast.toast(response.error, {
              title: 'Erro de Emissão',
              variant: 'danger',
              solid: true,
            })
          }
        })
        .catch(error => {
          console.error('Erro ao gerar PDF de Exame:', error)
          this.$bvToast.toast('Falha ao gerar a solicitação de exame.', {
            title: 'Erro de Emissão',
            variant: 'danger',
            solid: true,
          })
        })
    },
  },
}
</script>
